import React, { useState } from "react";
import { Card, Empty, ErrorState, Kpi, PageHeading, Rate, RowsSkeleton, Segmented, Table } from "../ui";
import { BarList } from "../charts";
import { useFilters } from "../filters";
import { useView } from "../data";
import { AGENT_LABELS, compact, fmt, ms, pct, statusOf } from "../format";

const speed = (v) => statusOf(v, { good: 10_000, bad: 30_000, higherIsBetter: false });

/** How long each agent, tool and model takes, and how often an agent run errors. */
export default function Latency() {
  const { params } = useFilters();
  const { data, loading, error } = useView("latency", params);
  const [kind, setKind] = useState("tools");
  const agents = data?.agents ?? [];
  const runs = agents.reduce((n, a) => n + a.count, 0);
  const errors = agents.reduce((n, a) => n + (a.errors ?? 0), 0);
  const slowest = [...agents].sort((a, b) => b.avgMs - a.avgMs)[0];
  const timing = kind === "tools" ? data?.tools ?? [] : data?.models ?? [];

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Speed & errors">How long each agent, tool and AI model takes, and how often an agent run errors.</PageHeading>

      <div className="aa-grid aa-grid-kpi">
        <Kpi loading={loading} label="Agent runs" value={fmt(runs)} foot="each time an agent worked on a request" />
        <Kpi loading={loading} label="Agent errors" value={fmt(errors)} status={runs ? statusOf(errors / runs, { good: 0.01, bad: 0.05, higherIsBetter: false }) : "none"} foot={runs ? `${pct(errors / runs, 1)} of runs` : null} />
        <Kpi loading={loading} label="Request time (median)" info="Whole requests, start to end, including time waiting on the user." value={ms(data?.runs?.p50)} foot={data?.runs?.p95 ? `p95 ${ms(data.runs.p95)}` : null} />
        <Kpi loading={loading} label="Slowest agent (avg)" value={slowest ? ms(slowest.avgMs) : "—"} status={slowest ? speed(slowest.avgMs) : "none"} foot={slowest?.name} />
      </div>

      <Card title="Agents" subtitle="Average time coloured: under 10s good, over 30s a problem" flush>
        {loading ? (
          <RowsSkeleton />
        ) : error ? (
          <ErrorState error={error} />
        ) : agents.length ? (
          <Table
            defaultSort={{ key: "count", dir: "desc" }}
            columns={[
              {
                key: "name",
                label: "Agent",
                render: (r) => (
                  <span>
                    <span className="aa-mono" style={{ fontWeight: 600, fontSize: 13 }}>
                      {r.name}
                    </span>
                    {AGENT_LABELS[r.name] && <span className="aa-sub">{AGENT_LABELS[r.name]}</span>}
                  </span>
                ),
              },
              { key: "count", label: "Runs", align: "right", render: (r) => fmt(r.count) },
              { key: "errors", label: "Errors", align: "right", render: (r) => <span style={{ color: r.errors ? "var(--aa-bad-ink)" : "var(--aa-faint)", fontWeight: r.errors ? 600 : 400 }}>{fmt(r.errors)}</span> },
              { key: "avgMs", label: "Average", align: "right", render: (r) => <Rate value={r.avgMs} format={ms} status={speed(r.avgMs)} /> },
              { key: "maxMs", label: "Slowest", align: "right", render: (r) => ms(r.maxMs) },
              { key: "inputTokens", label: "Tokens in", align: "right", render: (r) => compact(r.inputTokens) },
              { key: "outputTokens", label: "Tokens out", align: "right", render: (r) => compact(r.outputTokens) },
            ]}
            rows={agents.map((a) => ({ ...a, id: a.name }))}
          />
        ) : (
          <Empty icon="speed" title="No agent activity recorded yet" />
        )}
      </Card>

      <Card
        title={kind === "tools" ? "Tools" : "AI models"}
        subtitle="Average time per call, slowest first"
        actions={<Segmented value={kind} onChange={setKind} options={[["tools", "Tools"], ["models", "Models"]]} />}
      >
        {timing.length ? (
          <BarList
            limit={12}
            labelHeader="Name"
            valueLabel="Avg time"
            format={ms}
            color={kind === "tools" ? "var(--aa-s1)" : "var(--aa-s5)"}
            rows={[...timing]
              .sort((a, b) => b.avgMs - a.avgMs)
              .map((t) => ({ key: t.name, label: <span className="aa-mono">{t.name}</span>, value: t.avgMs, note: `×${compact(t.count)}`, title: `${t.name} — ${fmt(t.count)} calls, slowest ${ms(t.maxMs)}` }))}
          />
        ) : (
          <Empty compact title={`No ${kind} activity recorded yet`} />
        )}
      </Card>
    </div>
  );
}
